import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Consulta.css';

const Consulta = () => {
  const { user } = useAuth();
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [assunto, setAssunto] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tiposDocumentos, setTiposDocumentos] = useState([]);

  // Carregar tipos de documentos
  useEffect(() => {
    carregarTiposDocumentos();
  }, []);

  const carregarTiposDocumentos = async () => {
    try {
      const response = await api.get('/tipos-documentos');
      if (response.data.success) {
        setTiposDocumentos(response.data.data || []);
      }
    } catch (err) {
      // Se não houver endpoint, usar tipos padrão
      console.warn('Endpoint de tipos de documentos não encontrado, usando tipos padrão');
      setTiposDocumentos([
        { id_tipo_documento: 1, sigla: 'memorando', descricao: 'Memorando' },
        { id_tipo_documento: 2, sigla: 'oficio', descricao: 'Ofício' },
        { id_tipo_documento: 3, sigla: 'oficio_circular', descricao: 'Ofício Circular' },
        { id_tipo_documento: 4, sigla: 'requerimento', descricao: 'Requerimento' },
        { id_tipo_documento: 5, sigla: 'outro', descricao: 'Outro' }
      ]);
    }
  };

  // Função de busca com debounce
  const buscarProcessos = useCallback(async () => {
    // Não buscar se não houver critério de busca
    if (!numeroProcesso.trim() && !assunto.trim() && !tipoDocumento) {
      setResultados([]);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = {};
      if (numeroProcesso.trim()) {
        params.numero = numeroProcesso.trim();
      }
      if (assunto.trim()) {
        params.assunto = assunto.trim();
      }
      if (tipoDocumento) {
        params.tipo_documento = tipoDocumento;
      }

      const response = await api.get('/protocolos/consulta', { params });

      if (response.data.success) {
        setResultados(response.data.data || []);
      } else {
        setError('Erro ao buscar processos');
        setResultados([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar processos');
      setResultados([]);
    } finally {
      setLoading(false);
    }
  }, [numeroProcesso, assunto, tipoDocumento]);

  // Debounce: buscar após 500ms sem digitação
  useEffect(() => {
    const timer = setTimeout(() => {
      buscarProcessos();
    }, 500);

    return () => clearTimeout(timer);
  }, [buscarProcessos]);

  const getTipoLabel = (tipo) => {
    const tipos = {
      'memorando': 'Memorando',
      'oficio': 'Ofício',
      'oficio_circular': 'Ofício Circular',
      'requerimento': 'Requerimento',
      'outro': 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'rascunho': 'Rascunho',
      'protocolado': 'Protocolado',
      'em_tramitacao': 'Em Tramitação',
      'arquivado': 'Arquivado',
      'cancelado': 'Cancelado',
      'enviado': 'Enviado',
      'recebido': 'Recebido',
      'em_analise': 'Em Análise',
      'respondido': 'Respondido'
    };
    return statusLabels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      'rascunho': 'status-rascunho',
      'protocolado': 'status-protocolado',
      'em_tramitacao': 'status-tramitacao',
      'arquivado': 'status-arquivado',
      'cancelado': 'status-cancelado',
      'enviado': 'status-enviado',
      'recebido': 'status-recebido',
      'em_analise': 'status-analise',
      'respondido': 'status-respondido'
    };
    return classes[status] || 'status-default';
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="consulta-container">
      <div className="consulta-header">
        <h1>🔍 Consulta de Processos</h1>
        <p>Busque processos por número, assunto ou tipo de documento</p>
      </div>

      <div className="consulta-filtros">
        <div className="filtro-group">
          <label htmlFor="tipo-documento">Tipo de Documento</label>
          <select
            id="tipo-documento"
            className="form-control"
            value={tipoDocumento}
            onChange={(e) => setTipoDocumento(e.target.value)}
          >
            <option value="">Todos os tipos</option>
            {tiposDocumentos.map((tipo) => (
              <option key={tipo.id_tipo_documento} value={tipo.sigla}>
                {tipo.descricao}
              </option>
            ))}
          </select>
        </div>

        <div className="filtro-group">
          <label htmlFor="numero-processo">Número do Processo</label>
          <input
            id="numero-processo"
            type="text"
            className="form-control"
            placeholder="Ex: MEM-000001/2025 ou MEMO.0101.2025"
            value={numeroProcesso}
            onChange={(e) => setNumeroProcesso(e.target.value)}
          />
        </div>

        <div className="filtro-group">
          <label htmlFor="assunto">Assunto / Nome</label>
          <input
            id="assunto"
            type="text"
            className="form-control"
            placeholder="Digite parte do assunto ou nome do processo"
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="consulta-loading">
          <div className="loading-spinner"></div>
          <p>Buscando processos...</p>
        </div>
      )}

      {!loading && resultados.length > 0 && (
        <div className="consulta-resultados">
          <div className="resultados-header">
            <h2>Resultados ({resultados.length})</h2>
          </div>
          <div className="resultados-list">
            {resultados.map((processo) => (
              <div key={processo.id_protocolo} className="resultado-item">
                <div className="resultado-header">
                  <div className="resultado-numero">
                    <strong>{processo.numero_protocolo}</strong>
                  </div>
                  <div className={`resultado-status ${getStatusClass(processo.estado_atual)}`}>
                    {getStatusLabel(processo.estado_atual)}
                  </div>
                </div>
                <div className="resultado-body">
                  <div className="resultado-info">
                    <div className="info-row">
                      <span className="info-label">Assunto:</span>
                      <span className="info-value">{processo.assunto || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Tipo:</span>
                      <span className="info-value">{getTipoLabel(processo.tipo_documento)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Estado:</span>
                      <span className="info-value">{getStatusLabel(processo.estado_atual)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Localização Atual:</span>
                      <span className="info-value">
                        {processo.localizacao_atual || 'Não informado'}
                      </span>
                    </div>
                    {processo.setor_origem && (
                      <div className="info-row">
                        <span className="info-label">Origem:</span>
                        <span className="info-value">
                          {processo.setor_origem.sigla || processo.setor_origem.nome_setor}
                        </span>
                      </div>
                    )}
                    {processo.setor_destino && (
                      <div className="info-row">
                        <span className="info-label">Destino:</span>
                        <span className="info-value">
                          {processo.setor_destino.sigla || processo.setor_destino.nome_setor}
                        </span>
                      </div>
                    )}
                    {processo.criador && (
                      <div className="info-row">
                        <span className="info-label">Criado por:</span>
                        <span className="info-value">{processo.criador.nome_completo}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="info-label">Data de Criação:</span>
                      <span className="info-value">{formatarData(processo.data_criacao)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && numeroProcesso.trim() === '' && assunto.trim() === '' && !tipoDocumento && (
        <div className="consulta-empty">
          <div className="empty-icon">🔍</div>
          <p>Digite o número do processo, assunto ou selecione um tipo de documento para começar a busca</p>
        </div>
      )}

      {!loading && !error && resultados.length === 0 && (numeroProcesso.trim() !== '' || assunto.trim() !== '' || tipoDocumento) && (
        <div className="consulta-empty">
          <div className="empty-icon">📭</div>
          <p>Nenhum processo encontrado com os critérios informados</p>
        </div>
      )}
    </div>
  );
};

export default Consulta;

